import { successContentGeneration, failContentGeneration } from 'actions/contentGeneration'
import type { ContentGenerationMode } from 'actions/contentGeneration'
import { changePrompt } from 'actions/prompt'
import actionTypes from 'constants/actionTypes'
import { selectFocus, selectSelected, selectNeighborhoodDeep } from 'selectors/graph'
import * as selectNodes from 'selectors/graph/nodes'
import * as selectEdges from 'selectors/graph/edges'
import selectPrompt from 'selectors/prompt'
import { createOneEdge, deleteOneEdge, editOneEdge } from 'actions/edges'
import { createOneNode, deleteOneNode, editOneNode } from 'actions/nodes'
import { createOneKind, editOneKind } from 'actions/kinds'
import { focusOne } from 'actions/graph'
import { getKey } from 'api/apiKey'
import { setContentGenerationStatus } from 'actions/contentGeneration'
import { createId } from 'lib/id'
import direction from 'constants/direction'

let currentGenerator: ContentGenerator | null = null

type RootState = import('types/state').RootState
type GraphNodeMap = import('types/domain').GraphNodeMap
type GraphEdgeMap = import('types/domain').GraphEdgeMap
type GraphFocus = import('types/domain').GraphFocus
type GraphSelected = import('types/domain').GraphSelected
type NeighborhoodResult = import('types/domain').NeighborhoodResult

const logging = {
  llmPrompts: true,
  llmRawResponses: false, // only use this if you wanna see the json response with usage etc. usually you'll want llmTextResponses below
  llmTextResponses: true,
  graphs: false,
  kinds: false,
  operations: true,
  workflowSteps: true,
  auditExplanation: true,
}

type KindStyles = {
  color: string
}

type GraphEntity = {
  id?: string
  content?: string
  kind?: string
  from?: string
  to?: string
  directionality?: string
  label?: string
  styles?: KindStyles
}

type IdMaps = {
  tmpToRealMap: Record<string, string>
  realToTmpMap: Record<string, string>
}

type LlmEffort = 'low' | 'medium' | 'high' | string

type ContentBlock = {
  type: 'text'
  text: string
  cache_control?: { type: 'ephemeral' }
}

type AnthropicMessage = {
  role: 'user' | 'assistant'
  content: ContentBlock[] | string
}

type LlmRequestOptions = {
  systemPrompt?: string
  userPrompt?: string
  anthropicMessages?: AnthropicMessage[]
  model?: string
  reasoning?: boolean
  effort?: LlmEffort
  prefill?: string
  temperature?: number
  outputSchema?: Record<string, unknown>
}

type LlmResponse = {
  content?: Array<{ type?: string; text?: string }>
}

type GraphOperation = Record<string, unknown>

type GraphDirectionality = 'DIRECTED' | 'UNDIRECTED' | 'REVERSE' | 'BIDIRECTIONAL'

type EditCreateNodeOperation = {
  op: 'create_node'
  id: string
  content: string
  kind?: string
}

type EditCreateEdgeOperation = {
  op: 'create_edge'
  id: string
  from: string
  to: string
  content?: string
  kind?: string
  directionality?: GraphDirectionality
}

type EditUpdateNodeOperation = {
  op: 'update_node'
  id: string
  content?: string
  kind?: string
}

type EditUpdateEdgeOperation = {
  op: 'update_edge'
  id: string
  content?: string
  kind?: string
  directionality?: GraphDirectionality
}

type EditDeleteNodeOperation = {
  op: 'delete_node'
  id: string
}

type EditDeleteEdgeOperation = {
  op: 'delete_edge'
  id: string
}

type EditUpdateKindOperation = {
  op: 'update_kind'
  id: string
  label?: string
  color?: string
}

type EditOperation =
  | EditCreateNodeOperation
  | EditCreateEdgeOperation
  | EditUpdateNodeOperation
  | EditUpdateEdgeOperation
  | EditDeleteNodeOperation
  | EditDeleteEdgeOperation
  | EditUpdateKindOperation

type ContentGenerationAction = {
  type?: string
  mode?: ContentGenerationMode
}

type MiddlewareStore = {
  getState: () => RootState
  dispatch: (action: unknown) => unknown
}

type NextFn = (action: ContentGenerationAction) => unknown

const CONTENT_GENERATION_REQUEST = String(actionTypes.CONTENT_GENERATION.REQUEST)

const selectNeighborhoodDeepForState = (state: RootState): NeighborhoodResult => {
  return selectNeighborhoodDeep(state) as NeighborhoodResult
}

const selectPromptForState = (state: RootState): string => {
  const promptState = {
    prompt: String((state as { prompt?: unknown }).prompt || ''),
  }
  return String(selectPrompt(promptState) || '')
}

type ContentGenerationRequestAction = {
  type: string
  mode?: ContentGenerationMode
}

const isContentGenerationRequestAction = (action: ContentGenerationAction): action is ContentGenerationRequestAction => {
  return action.type === CONTENT_GENERATION_REQUEST
}

const extractLlmText = (response: LlmResponse): string => {
  return response.content?.find((c) => c.type === 'text')?.text || '{}'
}

class ContentGenerator {
  store: MiddlewareStore
  mode: ContentGenerationMode
  apiKey: string
  _defaultModel: string
  newEntities: Record<string, GraphEntity>
  completedOperations: GraphOperation[]
  focusedEntity: GraphEntity | null
  first: string | null
  existingNodes: GraphNodeMap
  existingEdges: GraphEdgeMap
  prompt: string
  existingKinds: Record<string, unknown>
  focusedEntities: GraphEntity[]
  contextEntities: GraphEntity[]
  focusedGraph: string
  contextGraph: string
  themeColors: string[]
  fixedThemeColors: Record<string, string>
  colorIdx: number
  inputComponents: string[]
  summaryText: string
  toGenerate: string
  grammarInstructions: string[]
  nodesFormatString: string
  edgesFormatString: string
  graphEditOperationsString: string
  graphStructureInstructions: string[]
  fewShotExamples: string
  ids: IdMaps
  _dispatchQueue: Array<() => void>
  _drainTimer: ReturnType<typeof setInterval> | null
  _drainResolvers: Array<() => void>
  _usage: { input_tokens: number; output_tokens: number; cache_creation_input_tokens: number; cache_read_input_tokens: number }
  _entityBlocks: string[]
  _clauses: string[]
  _isCancelled: boolean
  _abortController: AbortController

  constructor(store: MiddlewareStore, mode: ContentGenerationMode = 'convert') {
    this.store = store
    this.mode = mode
    this.apiKey = getKey() || ''
    this._defaultModel = 'claude-opus-4-6'
    this._isCancelled = false
    this._abortController = new AbortController()
    const state = this.store.getState()
    this.newEntities = {} // {<id>: entity}
    this.completedOperations = []
    this._usage = { input_tokens: 0, output_tokens: 0, cache_creation_input_tokens: 0, cache_read_input_tokens: 0 }
    this._entityBlocks = []
    this._clauses = []
    this.focusedEntity = null
    this.first = null
    const focus = selectFocus(state) as GraphFocus
    this.existingNodes = selectNodes.all(state)
    this.existingEdges = selectEdges.all(state)
    const ss = selectSelected(state) as GraphSelected
    this.prompt = String(selectPromptForState(state) || '')
    const existingNeighborhood = selectNeighborhoodDeepForState(state)
    this.existingKinds = this.store.getState().graph?.kinds?.all || {}
    this.focusedEntities = [
      ...Object.keys(ss.edges).reduce((acc: GraphEntity[], id: string) => {
        const edge = this.existingEdges[id]
        if (!edge?.id) {
          return acc
        }
        acc.push({
          id: edge.id,
          content: edge.data?.content,
          kind: edge.data?.kind,
          from: edge.data?.from,
          to: edge.data?.to,
        })
        return acc
      }, []),
      ...Object.keys(ss.nodes).reduce((acc: GraphEntity[], id: string) => {
        const node = this.existingNodes[id]
        if (!node?.id) {
          return acc
        }
        acc.push({
          id: node.id,
          content: node.data?.content,
          kind: node.data?.kind,
        })
        return acc
      }, []),
    ]
    if (focus?.isNode) {
      const focusedNode = this.existingNodes[focus.id]
      if (focusedNode) {
      this.focusedEntity = {
          id: focusedNode.id,
          content: focusedNode.data?.content,
          kind: focusedNode.data?.kind,
        }
      }
    } else if (focus?.isEdge) {
      const focusedEdge = this.existingEdges[focus.id]
      if (focusedEdge) {
      this.focusedEntity = {
          id: focusedEdge.id,
          content: focusedEdge.data?.content,
          kind: focusedEdge.data?.kind,
          from: focusedEdge.data?.from,
          to: focusedEdge.data?.to,
        }
      }
    }
    if (this.focusedEntity) {
      this.focusedEntities.push(this.focusedEntity)
      if (this.focusedEntity.id) {
        this.first = this.focusedEntity.id
      }
    }
    this.contextEntities = [
      ...Object.keys(existingNeighborhood.edges).reduce((acc: GraphEntity[], id: string) => {
        const edge = this.existingEdges[id]
        if (!edge?.id) {
          return acc
        }
        acc.push({
          id: edge.id,
          content: edge.data?.content,
          kind: edge.data?.kind,
          from: edge.data?.from,
          to: edge.data?.to,
        })
        return acc
      }, []),
      ...Object.keys(existingNeighborhood.nodes).reduce((acc: GraphEntity[], id: string) => {
        const node = this.existingNodes[id]
        if (!node?.id) {
          return acc
        }
        acc.push({
          id: node.id,
          content: node.data?.content,
          kind: node.data?.kind,
        })
        return acc
      }, []),
    ]
    this.focusedGraph = this.focusedEntities.filter((x: GraphEntity) => !x.styles).map((x: GraphEntity) => this.serializeEntity(x)).join('\n')
    this.contextGraph = this.contextEntities.filter((x: GraphEntity) => !x.styles).map((x: GraphEntity) => this.serializeEntity(x)).join('\n')
    this.themeColors = [
      // '#D4D4D4' // noun
      // '#CE9178' // verb, subject, object
      '#569CD6',
      '#6A9955',
      '#4EC9B0',
      '#B5CEA8',
      '#CCCCCC',
      '#AEA8CE',
    ]
    this.fixedThemeColors = {
      noun: '#D4D4D4',
      verb: '#CE9178',
      subject: '#CE9178',
      object: '#CE9178',
    }
    this.colorIdx = Object.keys(this.existingKinds).length
    this.inputComponents = []
    this.summaryText = ''
    if (this.contextGraph.length > 0) {
      this.inputComponents.push('Context Graph')
      this.inputComponents.push('Focused Graph')
    }
    if (this.prompt.length > 0) {
      this.inputComponents.push('Prompt')
    }
    this.toGenerate = 'a knowledge graph'
    this.grammarInstructions = [
      // parts of speach
      'Nouns are ALWAYS nodes.',
      'Verbs are ALWAYS nodes.',
      'Adjectives are ALWAYS UNDIRECTED self loop edges to AND from the same noun node. Exception: participial adjectives (verb-derived words like "fascinated", "broken", "terrified") that have their own relations (e.g. "fascinated by X") MUST be verb nodes, not adjective self loops, so that other relations can attach to them.',
      'Adverbs are ALWAYS UNDIRECTED self loop edges to AND from the same verb node.',
      'Other 1-word descriptors are ALWAYS UNDIRECTED self loop edges to AND from the same node.',
      "Grammatical relations like subject, object, indirect object, adjunct, oblique, etc. are ALWAYS edges between the relevant nodes, using the relation type as the edge's kind. If possible, use a semantically meaningful preposition for these relation edges' content. Otherwise use the type as the content.",
      "Conjunctions like 'or' are ALWAYS represented as grouping nodes, with 'item' edges to the nodes they are grouping. However, 'and' MUST be represented instead with multiple identical edges rather than a conjunction node, to avoid unnecessary complexity.",
      "Possession is represented as a DIRECTED 'of' edge from possessum to possessor, e.g. (car)-[of]->(owner).",
      "Appositive or equality MUST be represented as a DIRECTED 'is' edge from the main noun to the appositive descriptor, e.g. (Sarah)-[is]->(sister), with 'appositive' kind, NOT as a verb node.",
      "Negation is represented as an UNDIRECTED 'not' self loop edge to and from the negated node, with 'negation' kind.",

      // special cases
      'Edges that have subject kind MUST be UNDIRECTED from the verb to the noun. Edges that have object-like kind (including indirect object, ablative, etc.) MUST be DIRECTED from the verb to the noun.',
      "Edges that have exactly 'subject' or 'object' kind have empty ('') content, but all other grammatical relation edges MUST have non-empty content.",

      // relation targeting
      'If a relation targets multiple nodes that are grouped with a conjunction, target that relation edge to the conjunction node, not to each individual node in the group.',
      'If a relation targets a node-phrase with adjectives or other descriptors, target that relation edge to the main noun node, not to the descriptor nodes.',
      "If a relation targets a possessive node-phrase, target that relation edge to the main possessum node, not to the possessor. E.g. in 'Peter drives the car of Mary', the 'drives' edge should target 'car', not 'Mary'.",

      // Prevent weirdness
      'Edges NEVER contain nouns or verbs.',
      'Nodes NEVER contain grammatical relations, conjunctions or possessiveness.',
      'Nodes NEVER contain adjectives or adverbs.',
      "NEVER ever ever EVER use 'map' or 'maps' nodes to mean 'models' or similar.",
      'NEVER use pronouns: always resolve to the explicit noun content.',
    ]
    this.nodesFormatString =
      'The JSON format for nodes is {"c": "<content>", "i": "<short id>", "k": "<part of speech>"}. Keys: c=content, i=id, k=kind.'
    this.edgesFormatString = [
      'The JSON format for edges is {"c": "<content>", "i": "<short id>", "f": "<from node id>", "t": "<to node id>", "d": "D|U", "k": "<grammatical relation>"}. Keys: c=content, i=id, f=from, t=to, d=directionality, k=kind.',
      'Regarding edge directionality: U means undirected (no arrowhead), D means directed (arrow head at `t` node end).',
    ].join('\n')
    this.graphEditOperationsString =
      'To create a node or edge, emit its JSON directly (node or edge format). To delete, emit {"x":"<id of node or edge to delete>"}. Detection: presence of "f" or "t" means edge, presence of "c" and "k" without "f" means node, presence of "x" means delete.'
    this.graphStructureInstructions = [
      `The entire graph (including ${this.contextGraph.length > 0 ? 'Context Graph, Focused Graph and ' : ''} all new graph content together) MUST be a single connected component. Do not leave orphaned nodes or orphaned connected components. But do not be inventive.`,

      "Do NOT include EITHER endpoint node's content in the content of edges.",

      "EVERY node's content MUST be human-readable text, not an id.",

      "EVERY edge's content MUST be human-readable text, not an id, and MUST NEVER be the same as that edge's id.",

      "Do NOT create unnecessary or redundant representations of the same concept — e.g. for 'political discourse' content, do NOT represent it as more than one of 'political' adjective loop on 'discourse' node, OR 'of' edge from 'discourse' to 'politics', OR single 'political discourse' node; choose one or the other as appropriate.",

      'Information in the graph should be coherent and easily understood. Avoid confusing or overly technical structures wherever possible.',

      'There MUST NOT be multiple edges between the same pair of nodes with the same content.',
    ]
    this.fewShotExamples = `The following are format examples only — they are NOT part of the actual task.

Example 1 (first clause, empty graph, no prior entities):
Full Content (reference): "Cosmology bridges questioning between science and religion—science seeks empirical evidence for the universe's origin and structure, while religion addresses purpose and meaning, with both traditions asking fundamental questions about existence and humanity's place within it."
Clause: "Cosmology bridges questioning between science and religion"
Output:
[
  {"c":"cosmology","i":"cosmology","k":"noun"},
  {"c":"bridges","i":"bridges","k":"verb"},
  {"c":"questioning","i":"questioning","k":"noun"},
  {"c":"science","i":"science","k":"noun"},
  {"c":"religion","i":"religion","k":"noun"},
  {"c":"","i":"bridges_cosmo_subj","f":"bridges","t":"cosmology","d":"U","k":"subject"},
  {"c":"","i":"bridges_quest_obj","f":"bridges","t":"questioning","d":"D","k":"object"},
  {"c":"between","i":"quest_sci_obl","f":"questioning","t":"science","d":"D","k":"oblique"},
  {"c":"between","i":"quest_rel_obl","f":"questioning","t":"religion","d":"D","k":"oblique"}
]

Example 2 (second clause from same content, with prior entities):
Full Content (reference): "Cosmology bridges questioning between science and religion—science seeks empirical evidence for the universe's origin and structure, while religion addresses purpose and meaning, with both traditions asking fundamental questions about existence and humanity's place within it."
New Entities already created:
{"c":"cosmology","i":"Rk3mXp9vLw2Q","k":"noun"}
{"c":"bridges","i":"Tn7jBc4hYs8F","k":"verb"}
{"c":"questioning","i":"Wm5nDf2kRt6J","k":"noun"}
{"c":"science","i":"Hv9qAg7xNe3P","k":"noun"}
{"c":"religion","i":"Ux4wKj6mCs1Z","k":"noun"}
{"c":"","i":"Jb8rLn3vFy5D","f":"Tn7jBc4hYs8F","t":"Rk3mXp9vLw2Q","d":"U","k":"subject"}
{"c":"","i":"Qe2tMh9wAx7G","f":"Tn7jBc4hYs8F","t":"Wm5nDf2kRt6J","d":"D","k":"object"}
{"c":"between","i":"Ys6pCk4nEv1R","f":"Wm5nDf2kRt6J","t":"Hv9qAg7xNe3P","d":"D","k":"oblique"}
{"c":"between","i":"Fw3dNr8jLm5T","f":"Wm5nDf2kRt6J","t":"Ux4wKj6mCs1Z","d":"D","k":"oblique"}
Clause: "Science seeks empirical evidence for the universe's origin and structure"
Output:
[
  {"c":"seeks","i":"seeks","k":"verb"},
  {"c":"evidence","i":"evidence","k":"noun"},
  {"c":"empirical","i":"ev_empirical","f":"evidence","t":"evidence","d":"U","k":"adjective"},
  {"c":"origin","i":"origin","k":"noun"},
  {"c":"structure","i":"structure","k":"noun"},
  {"c":"universe","i":"universe","k":"noun"},
  {"c":"of","i":"origin_univ_of","f":"origin","t":"universe","d":"D","k":"possession"},
  {"c":"of","i":"struct_univ_of","f":"structure","t":"universe","d":"D","k":"possession"},
  {"c":"","i":"seeks_sci_subj","f":"seeks","t":"Hv9qAg7xNe3P","d":"U","k":"subject"},
  {"c":"","i":"seeks_ev_obj","f":"seeks","t":"evidence","d":"D","k":"object"},
  {"c":"for","i":"ev_origin_obl","f":"evidence","t":"origin","d":"D","k":"oblique"},
  {"c":"for","i":"ev_struct_obl","f":"evidence","t":"structure","d":"D","k":"oblique"}
]

Example 3 (fourth clause from same content, with all prior entities):
Full Content (reference): "Cosmology bridges questioning between science and religion—science seeks empirical evidence for the universe's origin and structure, while religion addresses purpose and meaning, with both traditions asking fundamental questions about existence and humanity's place within it."
New Entities already created:
{"c":"cosmology","i":"Rk3mXp9vLw2Q","k":"noun"}
{"c":"bridges","i":"Tn7jBc4hYs8F","k":"verb"}
{"c":"questioning","i":"Wm5nDf2kRt6J","k":"noun"}
{"c":"science","i":"Hv9qAg7xNe3P","k":"noun"}
{"c":"religion","i":"Ux4wKj6mCs1Z","k":"noun"}
{"c":"","i":"Jb8rLn3vFy5D","f":"Tn7jBc4hYs8F","t":"Rk3mXp9vLw2Q","d":"U","k":"subject"}
{"c":"","i":"Qe2tMh9wAx7G","f":"Tn7jBc4hYs8F","t":"Wm5nDf2kRt6J","d":"D","k":"object"}
{"c":"between","i":"Ys6pCk4nEv1R","f":"Wm5nDf2kRt6J","t":"Hv9qAg7xNe3P","d":"D","k":"oblique"}
{"c":"between","i":"Fw3dNr8jLm5T","f":"Wm5nDf2kRt6J","t":"Ux4wKj6mCs1Z","d":"D","k":"oblique"}
{"c":"seeks","i":"Dp5sKm2nXv8R","k":"verb"}
{"c":"evidence","i":"Gt9wJf4hBc3L","k":"noun"}
{"c":"empirical","i":"Nq7xVr6jYe1M","f":"Gt9wJf4hBc3L","t":"Gt9wJf4hBc3L","d":"U","k":"adjective"}
{"c":"origin","i":"Zk2dTn8pHw5F","k":"noun"}
{"c":"structure","i":"Cm4gLs9vRx6Q","k":"noun"}
{"c":"universe","i":"Wj3bNh7kFt1D","k":"noun"}
{"c":"of","i":"Ps8mXc5rEv2G","f":"Zk2dTn8pHw5F","t":"Wj3bNh7kFt1D","d":"D","k":"possession"}
{"c":"of","i":"Hy6nBf3wKd9T","f":"Cm4gLs9vRx6Q","t":"Wj3bNh7kFt1D","d":"D","k":"possession"}
{"c":"","i":"Lr1pJg7xMv4Q","f":"Dp5sKm2nXv8R","t":"Hv9qAg7xNe3P","d":"U","k":"subject"}
{"c":"","i":"Vt5kRn2hDs8C","f":"Dp5sKm2nXv8R","t":"Gt9wJf4hBc3L","d":"D","k":"object"}
{"c":"for","i":"Xw9jFm4bNe3L","f":"Gt9wJf4hBc3L","t":"Zk2dTn8pHw5F","d":"D","k":"oblique"}
{"c":"for","i":"Bq6dHs1cYp7R","f":"Gt9wJf4hBc3L","t":"Cm4gLs9vRx6Q","d":"D","k":"oblique"}
{"c":"addresses","i":"Fn3kWp8vRt2X","k":"verb"}
{"c":"purpose","i":"Jm7dLs4hNe9Q","k":"noun"}
{"c":"meaning","i":"Qx5rBg1cYw6F","k":"noun"}
{"c":"","i":"Hk9nTf2jMs4D","f":"Fn3kWp8vRt2X","t":"Ux4wKj6mCs1Z","d":"U","k":"subject"}
{"c":"","i":"Rv6pXc3wEb8L","f":"Fn3kWp8vRt2X","t":"Jm7dLs4hNe9Q","d":"D","k":"object"}
{"c":"","i":"Wd1gKn7xFs5T","f":"Fn3kWp8vRt2X","t":"Qx5rBg1cYw6F","d":"D","k":"object"}
{"c":"while","i":"Tk8mRv3nBx5J","f":"Fn3kWp8vRt2X","t":"Dp5sKm2nXv8R","d":"D","k":"oblique"}
Clause: "Both traditions ask fundamental questions about existence and humanity's place within the universe"
Output:
[
  {"c":"traditions","i":"traditions","k":"noun"},
  {"c":"ask","i":"ask","k":"verb"},
  {"c":"questions","i":"questions","k":"noun"},
  {"c":"fundamental","i":"q_fundamental","f":"questions","t":"questions","d":"U","k":"adjective"},
  {"c":"existence","i":"existence","k":"noun"},
  {"c":"humanity","i":"humanity","k":"noun"},
  {"c":"place","i":"place","k":"noun"},
  {"c":"of","i":"place_human_of","f":"place","t":"humanity","d":"D","k":"possession"},
  {"c":"is","i":"sci_trad","f":"Hv9qAg7xNe3P","t":"traditions","d":"D","k":"appositive"},
  {"c":"is","i":"rel_trad","f":"Ux4wKj6mCs1Z","t":"traditions","d":"D","k":"appositive"},
  {"c":"","i":"ask_sci_subj","f":"ask","t":"Hv9qAg7xNe3P","d":"U","k":"subject"},
  {"c":"","i":"ask_rel_subj","f":"ask","t":"Ux4wKj6mCs1Z","d":"U","k":"subject"},
  {"c":"","i":"ask_q_obj","f":"ask","t":"questions","d":"D","k":"object"},
  {"c":"about","i":"q_exist_obl","f":"questions","t":"existence","d":"D","k":"oblique"},
  {"c":"about","i":"q_place_obl","f":"questions","t":"place","d":"D","k":"oblique"},
  {"c":"within","i":"place_univ_obl","f":"place","t":"Wj3bNh7kFt1D","d":"D","k":"oblique"}
]

Example 4 (first clause, non-empty context graph, no prior entities, different domain):
Context Graph:
{"c":"Maria","i":"Bg4tKn8wRx2V","k":"noun"}
{"c":"Tokyo","i":"Lp7mHc3jFs9N","k":"noun"}
Full Content (reference): "Maria photographed the busy market with her phone and later photographed the quiet temple for her travel blog."
Clause: "Maria photographed the busy market with Maria's phone and later Maria photographed the quiet temple for Maria's travel blog"
Output:
[
  {"c":"photographed","i":"photo1","k":"verb"},
  {"c":"photographed","i":"photo2","k":"verb"},
  {"c":"market","i":"market","k":"noun"},
  {"c":"busy","i":"market_busy","f":"market","t":"market","d":"U","k":"adjective"},
  {"c":"phone","i":"phone","k":"noun"},
  {"c":"temple","i":"temple","k":"noun"},
  {"c":"quiet","i":"temple_quiet","f":"temple","t":"temple","d":"U","k":"adjective"},
  {"c":"blog","i":"blog","k":"noun"},
  {"c":"travel","i":"blog_travel","f":"blog","t":"blog","d":"U","k":"adjective"},
  {"c":"of","i":"phone_maria_of","f":"phone","t":"Bg4tKn8wRx2V","d":"D","k":"possession"},
  {"c":"of","i":"blog_maria_of","f":"blog","t":"Bg4tKn8wRx2V","d":"D","k":"possession"},
  {"c":"","i":"photo1_maria_subj","f":"photo1","t":"Bg4tKn8wRx2V","d":"U","k":"subject"},
  {"c":"","i":"photo1_market_obj","f":"photo1","t":"market","d":"D","k":"object"},
  {"c":"with","i":"photo1_phone_obl","f":"photo1","t":"phone","d":"D","k":"oblique"},
  {"c":"","i":"photo2_maria_subj","f":"photo2","t":"Bg4tKn8wRx2V","d":"U","k":"subject"},
  {"c":"","i":"photo2_temple_obj","f":"photo2","t":"temple","d":"D","k":"object"},
  {"c":"for","i":"photo2_blog_obl","f":"photo2","t":"blog","d":"D","k":"oblique"},
  {"c":"later","i":"photo2_later","f":"photo2","t":"photo2","d":"U","k":"adverb"}
]`
    this.ids = {
      tmpToRealMap: {}, // {<temp id from LLM>: <real id in graph>}
      realToTmpMap: {}, // {<real id in graph>: <temp id from LLM>}
    }
    this._dispatchQueue = []
    this._drainTimer = null
    this._drainResolvers = []
  }

  cancel() {
    this._isCancelled = true
    this._abortController.abort()
    if (this._drainTimer != null) {
      clearInterval(this._drainTimer)
      this._drainTimer = null
    }
    const resolvers = this._drainResolvers.splice(0)
    for (const resolve of resolvers) {
      resolve()
    }
  }

  _refreshCaches() {
    const state = this.store.getState()
    this.existingNodes = selectNodes.all(state)
    this.existingEdges = selectEdges.all(state)
    this.existingKinds = state.graph?.kinds?.all || {}
  }

  serializeEntity(entity: GraphEntity): string {
    if (entity.from != null) {
      const compact: Record<string, string> = { c: entity.content || '' }
      compact.i = entity.id || ''
      compact.f = entity.from
      compact.t = entity.to || ''
      compact.d = entity.directionality === 'DIRECTED' ? 'D' : 'U'
      if (entity.kind != null) {
        compact.k = entity.kind
      }
      return JSON.stringify(compact)
    }
    const compact: Record<string, string> = { c: entity.content || '' }
    compact.i = entity.id || ''
    if (entity.kind != null) {
      compact.k = entity.kind
    }
    return JSON.stringify(compact)
  }

  createEntity(entity: GraphEntity) {
    this._refreshCaches()
    if (!entity.id) {
      return
    }
    const entityHasKind = 'kind' in entity && entity.kind != null
    const entityFrom = 'from' in entity ? entity.from : undefined
    const entityTo = 'to' in entity ? entity.to : undefined
    const currentNode = this.existingNodes[entity.id]
    const currentEdge = this.existingEdges[entity.id]
    const nodeExists = currentNode != null && !entityFrom && !currentNode.deleted
    const edgeExists = currentEdge != null && entityFrom != null && !currentEdge.deleted
    const entityIsKind = entity.styles != null
    const entityIsEdge = entityFrom != null
    if (entityHasKind && entity.kind && !this.existingKinds[entity.kind]) {
      let color: string
      if (entity.kind && this.fixedThemeColors[entity.kind]) {
        color = this.fixedThemeColors[entity.kind] || '#CCCCCC'
      } else {
        color = this.themeColors[this.colorIdx % this.themeColors.length] || '#CCCCCC'
        this.colorIdx++
      }
      if (logging.operations && logging.kinds) {
        console.log(`➕ <${entity.kind || ''}> ${color}`)
      }
      this.createEntity({
        id: entity.kind || '',
        label: entity.kind || '',
        styles: {
          color,
        },
      })
    }
    if (nodeExists || edgeExists) {
      return
    }
    if (entityIsKind) {
      if (!entity.id || entity.label == null || entity.styles == null) {
        return
      }
      if (this.existingKinds[entity.id]) {
        return
      }
      this.store.dispatch(
        createOneKind({
          id: entity.id,
          label: entity.label,
          styles: entity.styles,
        })
      )
      return
    }
    const tmpId = entity.id
    const newId = createId()
    this.ids.tmpToRealMap[tmpId] = newId
    this.ids.realToTmpMap[newId] = tmpId
    entity.id = newId
    this.newEntities[newId] = entity
    const createdEntityId = newId
    if (entityIsEdge && !this.existingEdges[createdEntityId]) {
      const mappedFrom = this.ids.tmpToRealMap[entityFrom || ''] || entityFrom
      const mappedTo = this.ids.tmpToRealMap[entityTo || ''] || entityTo
      // Deduplicate cross-clause edges: skip if an edge with same endpoints, content, and kind already exists
      const duplicateNewEntity = Object.entries(this.newEntities).find(([id, e]) =>
        id !== newId &&
        e.from === mappedFrom && e.to === mappedTo &&
        e.content === entity.content && e.kind === entity.kind
      )
      const duplicateExistingEdge = !duplicateNewEntity
        ? Object.entries(this.existingEdges).find(([, e]) =>
            e.data?.from === mappedFrom && e.data?.to === mappedTo &&
            e.data?.content === entity.content && e.data?.kind === entity.kind
          )
        : undefined
      if (duplicateNewEntity || duplicateExistingEdge) {
        delete this.newEntities[newId]
        if (duplicateNewEntity) {
          const [dupId, dupEntity] = duplicateNewEntity
          console.log(`---dedup edge--- skipping "${entity.content}" (${entity.kind}) ${mappedFrom}→${mappedTo}, duplicates newEntities[${dupId}] ${dupEntity.from}→${dupEntity.to}`)
        } else {
          const [dupId, dupEntity] = duplicateExistingEdge!
          console.log(`---dedup edge--- skipping "${entity.content}" (${entity.kind}) ${mappedFrom}→${mappedTo}, duplicates existingEdges[${dupId}] ${dupEntity.data?.from}→${dupEntity.data?.to}`)
        }
        return
      }
      if (mappedFrom) {
        entity.from = mappedFrom
      }
      if (mappedTo) {
        entity.to = mappedTo
      }
      if (!(entity.from && entity.to)) {
        return
      }
      if (logging.operations) {
        const fromContent = this.existingNodes[entity.from]?.data?.content || this.newEntities[entity.from]?.content || entity.from
        const toContent = this.existingNodes[entity.to]?.data?.content || this.newEntities[entity.to]?.content || entity.to
        console.log(
          `➕ (${fromContent})--[${entity.content}]--${entity.directionality === 'DIRECTED' ? '>' : ''}(${toContent})`
        )
      }
      const edgePayload: {
        id?: string
        from?: string
        to?: string
        content?: string
        kind?: string
        directionality?: string
      } = {
        from: entity.from,
        to: entity.to,
      }
      if (entity.id != null) {
        edgePayload.id = entity.id
      }
      if (entity.content != null) {
        edgePayload.content = entity.content
      }
      if (entity.kind != null) {
        edgePayload.kind = entity.kind
      }
      if (entity.directionality != null) {
        edgePayload.directionality = entity.directionality
      }
      this._dispatchQueue.push(() => this.store.dispatch(
        createOneEdge(edgePayload)
      ))
    } else if (!this.existingNodes[createdEntityId]) {
      if (logging.operations) {
        console.log(`➕ (${entity.content})`)
      }
      const nodePayload: { id?: string; content?: string; kind?: string } = {}
      if (entity.id != null) {
        nodePayload.id = entity.id
      }
      if (entity.content != null) {
        nodePayload.content = entity.content
      }
      if (entity.kind != null) {
        nodePayload.kind = entity.kind
      }
      this._dispatchQueue.push(() => {
        this.store.dispatch(createOneNode(nodePayload))
        if (entity.kind === 'verb') {
          this.store.dispatch(focusOne({ id: newId }) as unknown as { type: string })
        }
      })
    }
    if (this.first == null && entity.id) {
      this.first = entity.id
    }
  }

  deleteEntity(entityType: 'node' | 'edge' | undefined, id: string | undefined) {
    if (!id) {
      return
    }
    const realId = this.ids.tmpToRealMap[id] || id
    if (entityType === 'node') {
      this._dispatchQueue.push(() => this.store.dispatch(deleteOneNode({ id: realId, autoConfirm: true })))
    } else if (entityType === 'edge') {
      this._dispatchQueue.push(() => this.store.dispatch(deleteOneEdge({ id: realId, autoConfirm: true })))
    }
    delete this.newEntities[realId]
  }

  logEntity(entity: GraphEntity) {
    if (!logging.graphs) {
      return
    }
    this._refreshCaches()
    if (entity.from || entity.to) {
      //its an edge
      const fromNode = entity.from ? this.existingNodes[entity.from] : undefined
      const toNode = entity.to ? this.existingNodes[entity.to] : undefined
      console.log(
        `EDGE <${entity.kind}> (${fromNode?.data?.content} ${entity.from})-[${entity.content} (${entity.id})]-${entity.directionality === 'DIRECTED' ? '>' : ''}(${toNode?.data?.content} ${entity.to})`
      )
    } else if (entity.styles) {
      // its a kind
      if (!logging.kinds) {
        return
      }
      console.log(`KIND ${entity.label} Styles: ${JSON.stringify(entity.styles)}`)
    } else {
      // its a node
      console.log(`NODE <${entity.kind}>  ${entity.content} (${entity.id})`)
    }
  }

  _getLLMResponse({
    systemPrompt = '',
    userPrompt = '',
    anthropicMessages,
    model,
    reasoning = false,
    effort = 'low',
    prefill = '',
    temperature,
    outputSchema,
  }: LlmRequestOptions): Promise<string> {
    const resolvedModel = model || this._defaultModel
    if (logging.llmPrompts) {
      console.log('---SYSTEM PROMPT---\n', systemPrompt, '\n---USER PROMPT---\n', anthropicMessages ? JSON.stringify(anthropicMessages, null, 2) : userPrompt, '\n-----------------------')
    }
    let messages: AnthropicMessage[]
    if (anthropicMessages) {
      messages = [...anthropicMessages]
    } else {
      messages = [{ role: 'user', content: userPrompt }]
    }
    if (prefill) {
      messages.push({ role: 'assistant', content: prefill })
    }
    const system: ContentBlock[] | string = systemPrompt
      ? [{ type: 'text' as const, text: systemPrompt, cache_control: { type: 'ephemeral' as const } }]
      : ''
    const body: Record<string, unknown> = {
      system,
      messages,
      model: resolvedModel,
      max_tokens: 32768,
      ...(temperature != null ? { temperature } : {}),
      ...(outputSchema ? { output_config: { format: { type: 'json_schema', schema: outputSchema } } } : {}),
    }
    if (reasoning) {
      const budgetByEffort: Record<string, number> = { low: 2048, medium: 8192, high: 32768 }
      const budget = budgetByEffort[effort] || 8192
      body.thinking = { type: 'enabled', budget_tokens: budget }
      body.max_tokens = budget + 32768
    }
    const headers: Record<string, string> = {
      'x-api-key': this.apiKey,
      'Content-Type': 'application/json',
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    }
    const req = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    return this._getLLMResponsePromise(req)
  }

  _getLLMResponsePromise(req: RequestInit): Promise<string> {
    const doFetch = (retriesLeft: number): Promise<Response> => {
      return fetch('https://api.anthropic.com/v1/messages', {
        ...req,
        signal: this._abortController.signal,
      }).then((res) => {
        if (res.status === 429 && retriesLeft > 0) {
          const retryAfter = parseInt(res.headers.get('retry-after') || '', 10)
          const waitSeconds = (retryAfter > 0 && retryAfter <= 300) ? retryAfter : 60
          console.log(`⏳ Rate limited — waiting ${waitSeconds}s before retry (${retriesLeft} retries left)`)
          return new Promise<Response>(resolve => setTimeout(resolve, waitSeconds * 1000)).then(() => doFetch(retriesLeft - 1))
        }
        if (res.status === 529 && retriesLeft > 0) {
          console.log(`⏳ API overloaded (529) — waiting 60s before retry (${retriesLeft} retries left)`)
          return new Promise<Response>(resolve => setTimeout(resolve, 60000)).then(() => doFetch(retriesLeft - 1))
        }
        return res
      })
    }
    return doFetch(3)
      .then((res) => {
        if (!res.ok) {
          return res.text().then((t) => {
            throw new Error(`HTTP ${res.status}: ${t}`)
          })
        }
        return res.json()
      })
      .then((json: LlmResponse) => {
        if (logging.llmRawResponses) {
          console.log('---RAW RESPONSE---\n', json, '\n-----------------------')
        }
        const usage = (json as unknown as { usage?: Record<string, number> }).usage
        if (usage) {
          this._accumulateUsage(usage)
        }
        const text = extractLlmText(json)
        if (logging.llmTextResponses) {
          console.log('---TEXT RESPONSE---\n', text, '\n-----------------------')
        }
        return text
      })
  }

  generateContent() {
    if (this.contextEntities.length > 0) {
      if (logging.graphs) {
        console.log('---Context Graph---')
        this.contextEntities.forEach((e) => this.logEntity(e))
      }
    }
    if (this.focusedEntities.length > 0) {
      if (logging.graphs) {
        console.log('---Focused Graph---')
        this.focusedEntities.forEach((e) => this.logEntity(e))
      }
    }
    const workflowPromise = this.mode === 'edit'
      ? this.runEditMode()
      : this.getSummary()
          .then(() => {
            if (this._isCancelled) throw new Error('Cancelled')
            return this.splitIntoClauses(this.summaryText)
          })
          .then((clauses) => {
            if (this._isCancelled) throw new Error('Cancelled')
            return this.streamGraphClauses(clauses, { reasoning: false })
          })
          .then(() => {
            if (this._isCancelled) throw new Error('Cancelled')
            return this._drainQueue()
          })
          .then(() => {
            if (this._isCancelled) throw new Error('Cancelled')
            return this.connectClauses()
          })
          .then(() => {
            if (this._isCancelled) throw new Error('Cancelled')
            return this._drainQueue()
          })

    return workflowPromise
      .then(() => {
        if (this._isCancelled) throw new Error('Cancelled')
        this.store.dispatch(successContentGeneration())
        this.store.dispatch(changePrompt(''))
      })
      .catch((err: unknown) => {
        console.error('Content generation error:', err)
        this.store.dispatch(failContentGeneration())
      })
      .then(() => {
        const u = this._usage
        console.log(
          `📊 Token usage — input: ${u.input_tokens}, cache_read: ${u.cache_read_input_tokens}, cache_create: ${u.cache_creation_input_tokens}, output: ${u.output_tokens}`
        )
      })
  }

  getSummary() {
    if (this.mode !== 'generate') {
      this.summaryText = this.prompt
      return Promise.resolve(this.prompt)
    }

    this.store.dispatch(setContentGenerationStatus('Generate'))
    if (logging.workflowSteps) {
      console.log('---GETTING SUMMARY---')
    }
    const userPrompt = [
      `For the following ${this.inputComponents.join(' and ')}, respond with a 0-30 word conceptual explanation answer.`,
      'Do not make the response about exploring or explaining the information, but rather make it be about the information itself.',
      'The response should be a concise explanation that captures the essence of the information, without unnecessary detail.',

      ...(this.contextEntities.length
        ? [
            this.nodesFormatString,
            this.edgesFormatString,
            'Regarding edge directionality: U means undirected (no arrowhead), D means directed (arrow head at `t` node end), REVERSE means arrow head at `f` node end, BIDIRECTIONAL means arrowheads at both ends.',
          ]
        : []),

      ...(this.focusedGraph.length > 0 ? ['Every node in the Focused Graph must be addressed in the response.'] : []),

      `The purpose is exploring information${this.focusedGraph.length > 0 ? ' around the Focused Graph' : ''}${this.prompt.length > 0 ? ' based on Prompt' : ''}${this.contextGraph.length > 0 ? ', using Context Graph to understand what is already explored' : ''}.\n`,
      ...(this.prompt.length > 0
        ? [
            'The Prompt is coming from a user describing what they want to understand more.',
            'If the Prompt is a command to accomplish a specific expansion of the graph then generate the content to accomplish that expansion, but do NOT generate content describing the expansion or prompt itself.',
            'If the Prompt is a question then generate the content to answer the question, but do NOT generate content describing the question itself.',
          ]
        : []),

      ...(this.contextGraph.length > 0
        ? [
            'Do NOT rehash info that is already in the Context Graph, unless it is needed in order to accomplish the conceptual expansion. It is very important not to include redundant information that is already in the Context Graph!',
            'Do NOT explain the context graph. It is just there for context.',
            `The Context Graph: \n${this.contextGraph}\n`,
          ]
        : []),

      ...(this.focusedGraph.length > 0 ? [`The Focused Graph: \n${this.focusedGraph}\n`] : []),

      'Respond with only a plain text explanation answer without any formatting, markdown, or other syntax — just the text itself. Do NOT include any preamble or explanation about the response, just the concise conceptual explanation itself.\n',

      ...(this.prompt.length > 0 ? [`The Prompt: \n${this.prompt}\n`] : []),
    ].join('\n')
    const summaryOpts: LlmRequestOptions = { userPrompt, temperature: 0.3, model: 'claude-sonnet-4-6' }
    return this._getLLMResponse(summaryOpts).then((text: string) => {
      console.log(text)
      this.summaryText = text
      return text
    })
  }

  static EDIT_OUTPUT_SCHEMA = {
    type: 'array',
    items: {
      anyOf: [
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'create_node' },
            id: { type: 'string' },
            content: { type: 'string' },
            kind: { type: 'string' },
          },
          required: ['op', 'id', 'content'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'create_edge' },
            id: { type: 'string' },
            from: { type: 'string' },
            to: { type: 'string' },
            content: { type: 'string' },
            kind: { type: 'string' },
            directionality: { type: 'string', enum: ['DIRECTED', 'UNDIRECTED', 'REVERSE', 'BIDIRECTIONAL'] },
          },
          required: ['op', 'id', 'from', 'to'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'update_node' },
            id: { type: 'string' },
            content: { type: 'string' },
            kind: { type: 'string' },
          },
          required: ['op', 'id'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'update_edge' },
            id: { type: 'string' },
            content: { type: 'string' },
            kind: { type: 'string' },
            directionality: { type: 'string', enum: ['DIRECTED', 'UNDIRECTED', 'REVERSE', 'BIDIRECTIONAL'] },
          },
          required: ['op', 'id'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'delete_node' },
            id: { type: 'string' },
          },
          required: ['op', 'id'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'delete_edge' },
            id: { type: 'string' },
          },
          required: ['op', 'id'],
          additionalProperties: false,
        },
        {
          type: 'object',
          properties: {
            op: { type: 'string', const: 'update_kind' },
            id: { type: 'string' },
            label: { type: 'string' },
            color: { type: 'string' },
          },
          required: ['op', 'id'],
          additionalProperties: false,
        },
      ],
    },
  }

  runEditMode(): Promise<void> {
    this.store.dispatch(setContentGenerationStatus('Edit'))
    if (logging.workflowSteps) {
      console.log('---EDIT MODE PARSE---')
    }
    return this.getSummary()
      .then(() => {
        if (this._isCancelled) throw new Error('Cancelled')
        return this.parseEditOperations()
      })
      .then((operations) => {
        if (this._isCancelled) throw new Error('Cancelled')
        this.store.dispatch(setContentGenerationStatus('Edit Apply'))
        if (logging.workflowSteps) {
          console.log(`---EDIT MODE APPLY (${operations.length} OPS)---`)
        }
        for (const op of operations) {
          this.enqueueEditOperation(op)
        }
      })
      .then(() => {
        if (this._isCancelled) throw new Error('Cancelled')
        return this._drainQueue()
      })
  }

  _getFullGraphSnapshotText() {
    this._refreshCaches()
    const nodes = Object.values(this.existingNodes)
      .filter((node) => node && !node.deleted)
      .map((node) => {
        const compact: Record<string, string> = {
          id: node.id,
          content: String(node.data?.content || ''),
          kind: String(node.data?.kind || ''),
        }
        return JSON.stringify(compact)
      })

    const edges = Object.values(this.existingEdges)
      .filter((edge) => edge && !edge.deleted)
      .map((edge) => {
        const compact: Record<string, string> = {
          id: edge.id,
          from: String(edge.data?.from || ''),
          to: String(edge.data?.to || ''),
          content: String(edge.data?.content || ''),
          kind: String(edge.data?.kind || ''),
          directionality: String(edge.data?.directionality || direction.UNDIRECTED),
        }
        return JSON.stringify(compact)
      })

    const kinds = Object.entries(this.existingKinds)
      .map(([id, kind]) => {
        const data = (kind as { data?: { label?: string; styles?: Record<string, unknown> } } | undefined)?.data
        const styles = data?.styles || {}
        return JSON.stringify({
          id,
          label: String(data?.label || id),
          color: String(styles.color || ''),
          styles,
        })
      })
      .join('\n')

    return {
      nodes: nodes.join('\n'),
      edges: edges.join('\n'),
      kinds,
    }
  }

  parseEditOperations(): Promise<EditOperation[]> {
    const { nodes, edges, kinds } = this._getFullGraphSnapshotText()
    const systemPrompt = [
      'You are a graph editing planner.',
      'You convert a user command into graph editing operations for an existing graph.',
      'Return ONLY valid JSON matching the schema. Do not add commentary.',
      'If the user command is ambiguous, choose the best matching existing entity or edge based on available graph evidence and continue.',
      'For updates and deletes, target existing IDs from the graph snapshot.',
      'For create operations, use short human-readable temporary IDs. Later operations in this same response may reference those IDs.',
      'Directionality meanings: DIRECTED arrow to `to`, UNDIRECTED no arrow, REVERSE arrow to `from`, BIDIRECTIONAL arrows both ways.',
      'For edge flip requests, prefer update_edge with directionality REVERSE.',
      'For kind color changes, use update_kind with the kind ID and set color (hex string, for example #FF8800).',
    ].join('\n')

    const userPrompt = [
      'Available graph kinds (JSON lines):',
      kinds || '(none)',
      '',
      'Existing nodes (JSON lines):',
      nodes || '(none)',
      '',
      'Existing edges (JSON lines):',
      edges || '(none)',
      '',
      ...(this.focusedGraph.length > 0 ? ['Focused graph subset:', this.focusedGraph, ''] : []),
      ...(this.contextGraph.length > 0 ? ['Context neighborhood subset:', this.contextGraph, ''] : []),
      'User command:',
      this.summaryText,
    ].join('\n')

    return this._getLLMResponse({
      systemPrompt,
      userPrompt,
      model: 'claude-haiku-4-5',
      temperature: 0,
      outputSchema: ContentGenerator.EDIT_OUTPUT_SCHEMA,
    }).then((response: string) => {
      const parsed = JSON.parse(response)
      if (!Array.isArray(parsed)) {
        throw new Error('Edit mode response must be a JSON array of operations')
      }
      return parsed as EditOperation[]
    })
  }

  splitIntoClauses(text: string): Promise<string[]> {
    if (logging.workflowSteps) {
      console.log('---SPLITTING INTO CLAUSES---')
    }
    this.store.dispatch(setContentGenerationStatus('Split'))
    const userPrompt = [
      'Split the following text into independent clauses. Resolve all pronouns to their explicit noun referents.',
      'Unpack deeply nested noun phrases (e.g. appositives combined with possessives, or 3+ layers of modification) so that each clause has simple, flat structure. For example, "John asked the coffee shop owner\'s eldest daughter Sarah about the menu" should become multiple clauses like: "John asked Sarah about the menu", "Sarah is the eldest daughter of the coffee shop owner", etc. Do NOT unpack simple possessives or prepositional phrases that are already clear on their own — e.g. "the decorations on the city\'s Wyvern Bridge" does not need to be split further.',
      'Unpack compound nouns where the modifier is a meaningful entity in its own right. For example, "kingdom soldiers" should become "soldiers of the kingdom", "university professors" should become "professors of the university". Do NOT unpack compounds where the modifier is purely descriptive or classificatory — e.g. "coffee shop", "guard tower", "stone wall" should stay as-is.',
      'If a prepositional phrase could ambiguously attach to more than one head in the clause, split it into its own clause to make the attachment explicit.',
      'When a modifier, purpose, condition, or other dependent clause applies to a coordinated structure (a list joined by "and", "or", etc.), ensure the resulting clauses preserve the relationship between the modifier and ALL items in the coordination, not just the nearest one. Preserve the original verb structure — do not reduce verb phrases to just their object nouns.',
      'you MUST NOT simplify the *information* from the original text, even though the representation of the information will be simpler.',
      'You MUST NOT lose any conceptual information from the original text. Every relationship, description, and fact must be preserved — just restructured into simpler clauses. But do NOT generate redundant clauses that merely restate information already fully contained in another clause.',
      'Return a JSON array of strings, where each string is one independent clause with all pronouns resolved.',
      `\nThe text:\n${text}`,
    ].join('\n')
    return this._getLLMResponse({
      userPrompt,
      model: 'claude-opus-4-6',//'claude-haiku-4-5',
      temperature: 0,
      outputSchema: {
        type: 'object',
        properties: {
          clauses: { type: 'array', items: { type: 'string' } },
        },
        required: ['clauses'],
        additionalProperties: false,
      },
    }).then((response: string) => {
      try {
        const parsed = JSON.parse(response) as { clauses: string[] }
        const clauses = parsed.clauses
        this._clauses = clauses
        if (logging.workflowSteps) {
          console.log(`---SPLIT INTO ${clauses.length} CLAUSES---`)
          clauses.forEach((c, i) => console.log(`  ${i + 1}. ${c}`))
        }
        return clauses
      } catch(err){
        console.error('Failed to parse clause split response, using full text as single clause', err)
        return [text]
      }
    })
  }

  streamGraphClauses(clauses: string[], llmOpts: LlmRequestOptions = {}): Promise<unknown> {
    const clauseModel = 'claude-haiku-4-5'// 'claude-sonnet-4-5'
    return clauses.reduce((chain: Promise<unknown>, clause: string, idx: number) => {
      return chain.then(() => {
        if (logging.workflowSteps) {
          console.log(`---CLAUSE ${idx + 1}/${clauses.length}---`)
        }
        this.store.dispatch(setContentGenerationStatus(`Convert ${idx + 1}/${clauses.length}`))
        const task = [
          `Convert the following clause into a structured ${this.toGenerate} representation`,
          this.contextGraph.length > 0
            ? ' around the Focused Graph, using Context Graph to understand what is already explored, only creating content for new information that is not already in Context Graph or New Entities. Every node in the Focused Graph must be an endpoint of at least one new edge'
            : '',
        ].join('')
        return this.convertToGraph(task, clause, { ...llmOpts, model: clauseModel })
      })
    }, Promise.resolve())
  }

  _findConnectedComponents(): Set<string>[] {
    const nodeIds = new Set<string>()
    const adjacency = new Map<string, Set<string>>()

    const addNode = (id: string) => {
      if (!id) return
      nodeIds.add(id)
      if (!adjacency.has(id)) adjacency.set(id, new Set())
    }

    const addEdge = (from: string, to: string) => {
      if (!from || !to) return
      addNode(from)
      addNode(to)
      adjacency.get(from)!.add(to)
      adjacency.get(to)!.add(from)
    }

    for (const entity of this.contextEntities) {
      if (entity.styles) continue
      if (entity.from) {
        addEdge(entity.from, entity.to || '')
      } else if (entity.id) {
        addNode(entity.id)
      }
    }

    for (const [id, entity] of Object.entries(this.newEntities)) {
      if (entity.styles) continue
      if (entity.from) {
        addEdge(entity.from, entity.to || '')
      } else {
        addNode(id)
      }
    }

    const visited = new Set<string>()
    const components: Set<string>[] = []

    for (const nodeId of nodeIds) {
      if (visited.has(nodeId)) continue
      const component = new Set<string>()
      const queue = [nodeId]
      while (queue.length > 0) {
        const current = queue.shift()!
        if (visited.has(current)) continue
        visited.add(current)
        component.add(current)
        const neighbors = adjacency.get(current)
        if (neighbors) {
          for (const neighbor of neighbors) {
            if (!visited.has(neighbor)) queue.push(neighbor)
          }
        }
      }
      components.push(component)
    }

    return components
  }

  static DISCOURSE_CONNECTIVES = new Set([
    // temporal
    'then', 'meanwhile', 'afterward', 'afterwards', 'subsequently', 'previously',
    'earlier', 'later', 'before', 'after', 'since', 'until', 'once', 'whenever',
    'simultaneously', 'henceforth', 'thereafter', 'beforehand', 'already',
    'eventually', 'finally', 'initially', 'originally', 'lately', 'recently',
    'formerly', 'presently', 'immediately', 'soon',
    // causal
    'therefore', 'thus', 'hence', 'consequently', 'accordingly', 'because',
    'so', 'as a result', 'for this reason', 'due to', 'owing to',
    'thereby', 'wherefore',
    // contrast / concession
    'however', 'nevertheless', 'nonetheless', 'yet', 'still', 'although',
    'though', 'even though', 'whereas', 'while', 'whilst', 'conversely',
    'on the other hand', 'on the contrary', 'in contrast', 'instead',
    'rather', 'notwithstanding', 'regardless', 'despite', 'in spite of',
    'alternatively', 'otherwise',
    // elaboration / addition
    'furthermore', 'moreover', 'additionally', 'also', 'besides',
    'in addition', 'likewise', 'similarly', 'equally', 'correspondingly',
    'what is more', 'not only', 'incidentally',
    // purpose / condition
    'in order to', 'so that', 'provided that', 'unless', 'if',
    'assuming', 'given that', 'on condition that', 'otherwise',
    // summary / conclusion
    'in conclusion', 'in summary', 'to summarize', 'overall', 'altogether',
    'in short', 'ultimately', 'in the end',
    // clarification
    'in other words', 'that is', 'namely', 'specifically', 'in particular',
    'for example', 'for instance', 'indeed', 'in fact',
  ])

  _textContainsConnectives(): boolean {
    const lower = this.summaryText.toLowerCase()
    for (const connective of ContentGenerator.DISCOURSE_CONNECTIVES) {
      if (lower.includes(connective)) return true
    }
    return false
  }

  _buildConnectClausesContext(): ContentBlock {
    const allEntities = Object.values(this.newEntities)
      .filter(e => !e.styles)
      .map(e => this.serializeEntity(e))
      .join('\n')

    return {
      type: 'text',
      text: [
        this.nodesFormatString,
        this.edgesFormatString,
        '',
        ...(this.contextGraph.length > 0 ? [`Context Graph:\n${this.contextGraph}\n`] : []),
        `Entities in the graph:\n${allEntities}\n`,
        `The original text: ${this.summaryText}`,
        `The clauses:\n${this._clauses.map((c, i) => `${i + 1}. ${c}`).join('\n')}`,
      ].join('\n'),
      cache_control: { type: 'ephemeral' },
    }
  }

  static CONNECT_OUTPUT_SCHEMA = {
    type: 'object',
    properties: {
      operations: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            c: { type: 'string' },
            i: { type: 'string' },
            f: { type: 'string' },
            t: { type: 'string' },
            d: { type: 'string', enum: ['D', 'U'] },
            k: { type: 'string' },
          },
          required: ['c', 'i', 'f', 't', 'd', 'k'],
          additionalProperties: false,
        },
      },
    },
    required: ['operations'],
    additionalProperties: false,
  }

  _processConnectResponse(response: string, label: string) {
    const parsed = JSON.parse(response) as { operations: GraphOperation[] }
    if (logging.workflowSteps) {
      console.log(`  ${label} edges to create: ${parsed.operations.length}`)
      parsed.operations.forEach(op => console.log(`    ${op.f} --[${op.c} (${op.k})]-> ${op.t}`))
    }
    for (const op of parsed.operations) {
      this.enqueueOperation(op)
    }
  }

  connectClauses(): Promise<void> {
    if (this._clauses.length <= 1) {
      return Promise.resolve()
    }
    if (logging.workflowSteps) {
      console.log('---CONNECT CLAUSES---')
    }
    this.store.dispatch(setContentGenerationStatus('Connect'))
    const cachedContext = this._buildConnectClausesContext()
    const explicitConnectivesHandled = this._textContainsConnectives()
      ? this._connectExplicitConnectives(cachedContext)
      : Promise.resolve()

    return explicitConnectivesHandled.then(() => {
      const components = this._findConnectedComponents()
      if (logging.workflowSteps) {
        console.log(`  Found ${components.length} connected component(s)`)
      }
      if (components.length <= 1) {
        return
      }
      return this._connectDisconnectedComponents(cachedContext, components)
    }).catch((err: unknown) => {
      console.error('connectClauses error (non-fatal):', err)
    })
  }

  _connectExplicitConnectives(cachedContext: ContentBlock): Promise<void> {
    if (logging.workflowSteps) {
      console.log('  Step 1: Explicit connectives')
    }

    const instructionBlock: ContentBlock = {
      type: 'text',
      text: [
        'The original text contains explicit discourse connectives (e.g. "meanwhile", "therefore", "however", "then", "furthermore", etc.) that may not be represented in the graph.',
        '',
        'Identify any explicit discourse connectives in the original text that are NOT already captured as edges in the graph entities. For each missing connective:',
        '- Create ONE directed edge.',
        '- The edge content MUST be the connective word/phrase as it appears in the text.',
        '- Choose the most semantically appropriate node from each side of the connective as endpoints (usually verbs).',
        '- Set edge kind to the discourse relation type (temporal, causal, contrast, elaboration, complication, purpose, condition, proximity).',
        '- Use short, readable edge IDs.',
        '- Do NOT create any nodes — only edges between existing nodes.',
        '',
        'IMPORTANT: Only create edges for connectives that EXPLICITLY appear in the original text. Do NOT invent implied connectives. If all explicit connectives are already represented, return an empty operations array.',
      ].join('\n'),
    }

    const anthropicMessages: AnthropicMessage[] = [
      { role: 'user', content: [cachedContext, instructionBlock] },
    ]

    return this._getLLMResponse({
      anthropicMessages,
      model: 'claude-haiku-4-5',
      temperature: 0,
      outputSchema: ContentGenerator.CONNECT_OUTPUT_SCHEMA,
    }).then((response: string) => {
      this._processConnectResponse(response, 'Explicit connective')
    })
  }

  _connectDisconnectedComponents(cachedContext: ContentBlock, components: Set<string>[]): Promise<void> {
    if (logging.workflowSteps) {
      console.log('  Step 3: Implicit connectives for disconnected components')
    }

    const componentDescriptions = components.map((component, idx) => {
      const nodes = [...component]
        .map(id => this.newEntities[id] || this.contextEntities.find(e => e.id === id))
        .filter((e): e is GraphEntity => e != null && !e.from && !e.styles)
        .map(e => this.serializeEntity(e))
      const edges = Object.values(this.newEntities)
        .filter(e => e.from && !e.styles && component.has(e.from) && component.has(e.to || ''))
        .map(e => this.serializeEntity(e))
      const contextEdgeEntities = this.contextEntities
        .filter(e => e.from && !e.styles && e.id && component.has(e.from) && component.has(e.to || ''))
        .map(e => this.serializeEntity(e))
      const allEdges = [...edges, ...contextEdgeEntities]
      return `Component ${idx + 1}:\n${[...nodes, ...allEdges].join('\n')}`
    }).join('\n\n')

    const instructionBlock: ContentBlock = {
      type: 'text',
      text: [
        `The knowledge graph has ${components.length} disconnected components. Create edges to connect them, but ONLY where the original text implies a discourse relationship between the components.`,
        '',
        'For each pair of disconnected components that have an implied discourse relationship in the original text:',
        '- Create ONE directed edge connecting them.',
        '- Choose the most semantically appropriate node from each component as an endpoint (usually verbs).',
        '- Set edge content to the discourse connective (e.g. "then", "because", "however", "meanwhile").',
        '- Set edge kind to the discourse relation type (temporal, causal, contrast, elaboration, complication, purpose, condition, proximity).',
        '- Use short, readable edge IDs.',
        '- Do NOT create any nodes — only edges between existing nodes.',
        '',
        'IMPORTANT: If the original text does NOT imply a discourse relationship between two components, do NOT create an edge between them. Return an empty operations array if no edges are needed.',
        '',
        componentDescriptions,
      ].join('\n'),
    }

    const anthropicMessages: AnthropicMessage[] = [
      { role: 'user', content: [cachedContext, instructionBlock] },
    ]

    return this._getLLMResponse({
      anthropicMessages,
      model: 'claude-haiku-4-5',
      temperature: 0,
      outputSchema: ContentGenerator.CONNECT_OUTPUT_SCHEMA,
    }).then((response: string) => {
      this._processConnectResponse(response, 'Implicit connective')
    })
  }

  _buildGraphPrompts(task: string, { clause }: { clause?: string } = {}) {
    const systemPromptParts = [
      'You are a knowledge graph construction engine.',

      'You strictly follow the instructions and grammar rules below.',

      ...(this.contextGraph.length > 0 ? ['Use the Context Graph and Focused Graph for reference as needed.'] : []),

      ...(this._entityBlocks.length > 0 || Object.keys(this.newEntities).length > 0
        ? ['Use the New Entities to understand what graph content has already been created.']
        : []),

      'You create graph content by returning a JSON array (NOT NLJSON) of node and edge objects with absolutely no extra commentary or explanation. Do NOT wrap the response in a code block.',

      this.nodesFormatString,
      this.edgesFormatString,
      this.graphEditOperationsString,

      `EVERY graph edit you make strictly follows these grammar rules:\n - ${this.grammarInstructions.join('\n - ')}`,

      `EVERY graph edit you make strictly follows these graph structure rules:\n - ${this.graphStructureInstructions.join('\n - ')}`,

      this.fewShotExamples,

      "It's VERY VERY VERY important not to stop responding until all content from the clause is represented in the graph. This is the law. Do not ever deviate.",
      'You have been given ALL the rules to follow. Do NOT assume that there may be other rules. Do NOT assume any hypotheticals.'
    ]

    const systemPrompt = systemPromptParts.join('\n')

    // Build structured messages for Anthropic caching:
    // Static user message (cached) → entity blocks (one per prior clause) → clause message (varying)
    const staticContent: ContentBlock[] = [
      {
        type: 'text',
        text: [
          `Your task is to convert the clause at the end of this message into a structured graph representation, using the following ${this.inputComponents.join(', ')}.`,

          'Not every word in The Content needs to be a node or edge in the graph as long as the meaning is conveyed.',

          'The Full Content is provided below for reference context only. Your task is ONLY to convert the clause specified at the end of this message, not the full content.',

          'However the Full Content provides essential context for interpreting each clause. Use it to resolve ambiguity, understand implicit references, correctly parse relationships that span across the broader text, and determine if multiple uses of the same word refer to the same instance or different instances.',

          ...(this.contextGraph.length > 0 ? [`The Context Graph: \n${this.contextGraph}\n`] : []),

          ...(this.focusedGraph.length > 0 ? [`The Focused Graph: \n${this.focusedGraph}\n`] : []),

          `The Full Content (reference context only): \n${this.summaryText}\n`,
          'As a reminder: do NOT include ANY content in the response except the JSON array: no explanation text, no code block wrapper, NOTHING.',
        ].join('\n'),
        cache_control: { type: 'ephemeral' },
      },
    ]

    const entityBlocks: ContentBlock[] = this._entityBlocks.map((block, i) => ({
      type: 'text' as const,
      text: `New Entities already created:\n${block}`,
      ...(i === this._entityBlocks.length - 1 ? { cache_control: { type: 'ephemeral' as const } } : {}),
    }))

    const clauseText = `The clause to convert: "${clause || task}"`
    const clauseBlock: ContentBlock = {
      type: 'text',
      text: clauseText,
    }

    const anthropicMessages: AnthropicMessage[] = [
      { role: 'user', content: [...staticContent, ...entityBlocks, clauseBlock] },
    ]

    return { systemPrompt, anthropicMessages }
  }

  _buildAuditItems(operations: GraphOperation[]): Record<string, { content: string; sameAs?: string[]; reusedBy?: string[] }> {
    const auditItems: Record<string, { content: string; sameAs?: string[]; reusedBy?: string[] }> = {}

    // Collect all existing node contents (Context Graph + New Entities) keyed by content
    const existingByContent: Record<string, string[]> = {}
    for (const entity of this.contextEntities) {
      if (entity.content && entity.id && !entity.from) {
        const key = entity.content.toLowerCase()
        if (!existingByContent[key]) existingByContent[key] = []
        existingByContent[key].push(entity.id)
      }
    }
    for (const [id, entity] of Object.entries(this.newEntities)) {
      if (entity.content && !entity.from) {
        const key = entity.content.toLowerCase()
        if (!existingByContent[key]) existingByContent[key] = []
        if (!existingByContent[key].includes(id)) existingByContent[key].push(id)
      }
    }

    // Find new nodes whose content matches an existing node
    for (const op of operations) {
      if ('c' in op && 'i' in op && !('f' in op) && !('x' in op)) {
        const content = String(op.c || '')
        const id = String(op.i || '')
        const key = content.toLowerCase()
        if (existingByContent[key]) {
          auditItems[id] = { content, sameAs: existingByContent[key] }
        }
      }
    }

    // Find reused existing IDs that appear as edge endpoints in the output
    const newNodeIds = new Set(
      operations
        .filter(op => 'c' in op && 'i' in op && !('f' in op) && !('x' in op))
        .map(op => String(op.i || ''))
    )
    const reusedEdges: Record<string, string[]> = {}
    for (const op of operations) {
      if ('f' in op || 't' in op) {
        const edgeId = String(op.i || '')
        const from = String(op.f || '')
        const to = String(op.t || '')
        if (from && !newNodeIds.has(from)) {
          if (!reusedEdges[from]) reusedEdges[from] = []
          reusedEdges[from].push(edgeId)
        }
        if (to && !newNodeIds.has(to)) {
          if (!reusedEdges[to]) reusedEdges[to] = []
          reusedEdges[to].push(edgeId)
        }
      }
    }
    for (const [id, edgeIds] of Object.entries(reusedEdges)) {
      if (auditItems[id]) continue // already covered by sameAs
      const entity = this.newEntities[id] || this.contextEntities.find(e => e.id === id)
      if (entity?.content && !entity.from) {
        auditItems[id] = { content: entity.content, reusedBy: edgeIds }
      }
    }

    return auditItems
  }

  _auditNodeReuse(
    operations: GraphOperation[],
    clause: string,
    auditItems: Record<string, { content: string; sameAs?: string[]; reusedBy?: string[] }>,
  ): Promise<{ id: string; action: string; target: string; explanation: string }[]> {
    if (logging.workflowSteps) {
      console.log('---AUDIT NODE REUSE---')
      console.log('  Items:', JSON.stringify(auditItems, null, 2))
    }

    const staticContent: ContentBlock = {
      type: 'text',
      text: [
        'You are auditing a knowledge graph conversion step for correct entity co-reference. Sometimes the converter creates a new node for a concept that is already represented by an existing node, or reuses an existing node for a concept that should be a new instance. Your task is to identify these cases and determine the correct identity for each node in question.',
        '',
        this.nodesFormatString,
        this.edgesFormatString,
        '',
        `The Full Content being converted: "${this.summaryText}"`,
        '',
        ...(this.contextGraph.length > 0 ? [`Context Graph:\n${this.contextGraph}\n`] : []),
        'For each item in the audit list, determine whether the node in the converter output should be co-referential with the existing entity (same token) or refers to a distinct instance (same type only). For nouns, token identity means the same individual entity — a specific house differs from another specific house and from houses in general. For verbs, token identity means the same event token — a specific act of X by actor P at time T differs from another occurrence of X by P, and from the general action type.',
        'Each item has either a "sameAs" field or a "reusedBy" field:',
        '- "sameAs": the converter created a new node with the same content as these existing nodes. Should it reuse one of them instead, or is this a genuinely different instance?',
        '- "reusedBy": the converter reused this node as an endpoint of these edges. In the current clause, does this word refer to the same instance as the one created earlier, or a different one?',
        'Examine how each node is used in the Full Content, New Entities, and Context Graph — look at its relationships, possessors, and surrounding context — to determine whether the current clause refers to the same instance or a distinct one.',
        'Same instance = the same real-world entity or event, even if mentioned in different contexts or from different angles. Different instance = a genuinely separate entity (e.g. one person\'s car vs another person\'s car, or an action at one time vs a similar action at another time). Two mentions of the same place, person, or thing in different parts of the text are still the SAME instance.',
        '',
        'Respond with a "decisions" array. For each item, provide:',
        '- "id": the node ID from the audit list',
        '- "explanation": brief reasoning for the decision.',
        '- "action": "reuse" if the node should reuse an existing entity, or "new" if it is a separate instance. Note that this is independent from whether the converter actually reused an existing node or created a new one — your decision should be based on the meaning and identity of the concept, not on what the converter did. Be careful that the action matches the explanation! Its very important not to get this wrong!',
        '- "target": the existing ID to reuse (when action is "reuse"), or empty string (when action is "new")',
      ].join('\n'),
      cache_control: { type: 'ephemeral' },
    }

    const entityBlocks: ContentBlock[] = this._entityBlocks.map((block, i) => ({
      type: 'text' as const,
      text: `New Entities:\n${block}`,
      ...(i === this._entityBlocks.length - 1 ? { cache_control: { type: 'ephemeral' as const } } : {}),
    }))

    const clauseContent: ContentBlock = {
      type: 'text',
      text: [
        `The clause being converted in this workflow step (note that this was extracted from The Full Content above): "${clause}"`,
        '',
        'The converter produced this output:',
        JSON.stringify(operations),
        '',
        'Items to audit:',
        JSON.stringify(auditItems),
      ].join('\n'),
    }

    const anthropicMessages: AnthropicMessage[] = [
      { role: 'user', content: [staticContent, ...entityBlocks, clauseContent] },
    ]

    return (this._getLLMResponse({
      anthropicMessages,
      model: 'claude-haiku-4-5',
      temperature: 0,
      outputSchema: {
        type: 'object',
        properties: {
          decisions: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string' },
                explanation: { type: 'string' },
                action: { type: 'string', enum: ['reuse', 'new'] },
                target: { type: 'string' },
              },
              required: ['id', 'action', 'target', 'explanation'],
              additionalProperties: false,
            },
          },
        },
        required: ['decisions'],
        additionalProperties: false,
      },
    }) as Promise<string>).then((response: string) => {
      try {
        const parsed = JSON.parse(response) as { decisions: { id: string; action: string; target: string; explanation: string }[] }
        if (logging.workflowSteps) {
          console.log('  Decisions:', JSON.stringify(parsed.decisions, null, 2))
        }
        return parsed.decisions
      } catch {
        console.error('Failed to parse audit response, skipping audit')
        return []
      }
    })
  }

  _applyAuditDecisions(
    operations: GraphOperation[],
    decisions: { id: string; action: string; target: string; explanation: string }[],
  ): { operations: GraphOperation[]; newVerbIds: string[] } {
    const remaps: Record<string, string> = {}
    const newVerbIds: string[] = []

    for (const decision of decisions) {
      const id = decision.id
      if (decision.action === 'reuse' && decision.target && decision.target !== id) {
        // Over-creation: converter made a new node but should reuse existing
        remaps[id] = decision.target
      } else if (decision.action === 'new') {
        // Over-reuse: converter reused an existing ID but it should be a new node
        // Check if this ID is an existing entity being reused as an edge endpoint
        const isReusedExisting = !operations.some(
          op => 'c' in op && 'i' in op && !('f' in op) && String(op.i) === id
        )
        if (isReusedExisting) {
          // Need to create a new node and remap edges to it
          const entity = this.newEntities[id] || this.contextEntities.find(e => e.id === id)
          if (entity?.content) {
            const newId = `audit_${id}`
            remaps[id] = newId
            // Insert a new node operation at the beginning
            operations = [
              { c: entity.content, i: newId, k: entity.kind || 'noun' },
              ...operations,
            ]
            if (entity.kind === 'verb') {
              newVerbIds.push(newId)
            }
          }
        }
      }
    }

    if (Object.keys(remaps).length === 0) return { operations, newVerbIds }

    // Apply remaps: remove nodes that are being reused, update edge endpoints
    const remappedOps = operations.reduce((acc: GraphOperation[], op) => {
      if ('c' in op && 'i' in op && !('f' in op) && !('x' in op)) {
        const id = String(op.i || '')
        if (remaps[id] && remaps[id] !== id) {
          // This node is being remapped to an existing one — drop it
          if (logging.workflowSteps) {
            console.log(`  Audit: dropping node "${op.c}" (${id}), reusing ${remaps[id]}`)
          }
          return acc
        }
      }
      if ('f' in op || 't' in op) {
        const from = String(op.f || '')
        const to = String(op.t || '')
        if (remaps[from]) op = { ...op, f: remaps[from] }
        if (remaps[to]) op = { ...op, t: remaps[to] }
      }
      acc.push(op)
      return acc
    }, [])
    return { operations: remappedOps, newVerbIds }
  }

  _repairNewVerbEdges(operations: GraphOperation[], clause: string, newVerbIds: string[]): Promise<GraphOperation[]> {
    const verbDescriptions = newVerbIds.map(id => {
      const node = operations.find(op => 'c' in op && 'i' in op && !('f' in op) && String(op.i) === id)
      return node ? `  - "${node.c}" (id: ${id})` : `  - id: ${id}`
    }).join('\n')

    if (logging.workflowSteps) {
      console.log('---REPAIR NEW VERB EDGES---')
      console.log(`  Verbs to check:\n${verbDescriptions}`)
    }

    const staticContent: ContentBlock = {
      type: 'text',
      text: [
        'You are auditing a knowledge graph conversion step. New verb nodes were just created for a clause, but they may be missing grammatical relation edges (subject, object, indirect object, oblique, adjunct, etc.) that the clause implies.',
        '',
        this.nodesFormatString,
        this.edgesFormatString,
        '',
        `The Full Content: "${this.summaryText}"`,
        '',
        ...(this.contextGraph.length > 0 ? [`Context Graph:\n${this.contextGraph}\n`] : []),
        'Examine the clause and the current operations list. For each new verb node listed below, determine if any grammatical relation edges implied by the clause are missing from the operations. Return ONLY the additional edge operations needed to complete the verb\'s relations.',
        '',
        'Rules:',
        '- Do NOT create any nodes — only edges between existing nodes.',
        '- Do NOT duplicate edges already present in the operations list.',
        '- Only create edges that are implied by the clause for the listed verb nodes.',
        '- Subject edges are UNDIRECTED from verb to noun. Object-like edges (object, indirect object, oblique, etc.) are DIRECTED from verb to noun.',
        '- If no edges are missing, return an empty operations array.',
        '',
        'New verb nodes to check:',
        verbDescriptions,
      ].join('\n'),
      cache_control: { type: 'ephemeral' },
    }

    const entityBlocks: ContentBlock[] = this._entityBlocks.map((block, i) => ({
      type: 'text' as const,
      text: `New Entities:\n${block}`,
      ...(i === this._entityBlocks.length - 1 ? { cache_control: { type: 'ephemeral' as const } } : {}),
    }))

    const clauseContent: ContentBlock = {
      type: 'text',
      text: [
        `The clause: "${clause}"`,
        '',
        'Current operations for this clause (post-audit):',
        JSON.stringify(operations),
      ].join('\n'),
    }

    const anthropicMessages: AnthropicMessage[] = [
      { role: 'user', content: [staticContent, ...entityBlocks, clauseContent] },
    ]

    return this._getLLMResponse({
      anthropicMessages,
      model: 'claude-haiku-4-5',
      temperature: 0,
      outputSchema: ContentGenerator.CONNECT_OUTPUT_SCHEMA,
    }).then((response: string) => {
      const parsed = JSON.parse(response) as { operations: GraphOperation[] }
      if (logging.workflowSteps) {
        console.log(`  Repair edges added: ${parsed.operations.length}`)
        if (parsed.operations.length > 0) {
          console.log('  Edges:', JSON.stringify(parsed.operations, null, 2))
        }
      }
      return [...operations, ...parsed.operations]
    })
  }

  convertToGraph(task: string, clause: string, llmOpts: LlmRequestOptions = {}, retriesLeft = 3): Promise<void> {
    if (logging.workflowSteps) {
      console.log(`---CONVERT: ${clause}---`)
    }
    const { systemPrompt, anthropicMessages } = this._buildGraphPrompts(task, { clause })
    return this._getLLMResponse({
      ...llmOpts,
      systemPrompt,
      anthropicMessages,
      temperature: 0,
      outputSchema: {
        type: 'object',
        properties: {
          operations: {
            type: 'array',
            items: {
              anyOf: [
                {
                  type: 'object',
                  properties: {
                    c: { type: 'string' },
                    i: { type: 'string' },
                    k: { type: 'string' },
                  },
                  required: ['c', 'i', 'k'],
                  additionalProperties: false,
                },
                {
                  type: 'object',
                  properties: {
                    c: { type: 'string' },
                    i: { type: 'string' },
                    f: { type: 'string' },
                    t: { type: 'string' },
                    d: { type: 'string', enum: ['D', 'U'] },
                    k: { type: 'string' },
                  },
                  required: ['c', 'i', 'f', 't', 'd', 'k'],
                  additionalProperties: false,
                },
                {
                  type: 'object',
                  properties: {
                    x: { type: 'string' },
                  },
                  required: ['x'],
                  additionalProperties: false,
                },
              ],
            },
          },
        },
        required: ['operations'],
        additionalProperties: false,
      },
    }).then((response: string) => {
      const parsed = JSON.parse(response) as { operations: GraphOperation[] }
      const operations = parsed.operations

      // Audit node reuse decisions
      const auditItems = this._buildAuditItems(operations)
      const auditPromise = Object.keys(auditItems).length > 0
        ? this._auditNodeReuse(operations, clause, auditItems).then(decisions => {
            const { operations: auditedOps, newVerbIds } = this._applyAuditDecisions(operations, decisions)
            if (newVerbIds.length > 0) {
              return this._repairNewVerbEdges(auditedOps, clause, newVerbIds)
            }
            return auditedOps
          })
        : Promise.resolve(operations)

      return auditPromise.then(auditedOperations => {
        const finalOperations = this.reorderOperations(auditedOperations)
        const existingIds = new Set(Object.keys(this.newEntities))
        for (const op of finalOperations) {
          this.enqueueOperation(op)
        }
        const clauseEntities = Object.entries(this.newEntities)
          .filter(([id]) => !existingIds.has(id))
          .map(([, entity]) => entity)
          .filter(e => !e.styles)
        if (clauseEntities.length > 0) {
          this._entityBlocks.push(
            clauseEntities.map(e => this.serializeEntity(e)).join('\n')
          )
        }
      })
    }).catch((err: unknown) => {
      const isNetworkError = err instanceof TypeError && /network|fetch/i.test(err.message)
      if (isNetworkError && retriesLeft > 0) {
        console.log(`---RETRY ${4 - retriesLeft}/3--- Network error, retrying convertToGraph (${retriesLeft - 1} retries remaining)`)
        return this.convertToGraph(task, clause, llmOpts, retriesLeft - 1)
      }
      throw err
    })
  }

  reorderOperations(operations: GraphOperation[]): GraphOperation[] {
    const result: GraphOperation[] = []
    const pendingEdges: GraphOperation[] = []
    const emittedNodeIds = new Set<string>()

    for (const op of operations) {
      if ('c' in op && 'i' in op && !('f' in op) && !('x' in op)) {
        // Node: emit it and flush any pending edges whose endpoints are now satisfied
        emittedNodeIds.add(String(op.i || ''))
        result.push(op)
        let i = 0
        while (i < pendingEdges.length) {
          const edge = pendingEdges[i]!
          const from = String(edge.f || '')
          const to = String(edge.t || '')
          const fromReady = emittedNodeIds.has(from) || !(operations.some(o => 'c' in o && 'i' in o && !('f' in o) && !('x' in o) && String(o.i || '') === from))
          const toReady = emittedNodeIds.has(to) || !(operations.some(o => 'c' in o && 'i' in o && !('f' in o) && !('x' in o) && String(o.i || '') === to))
          if (fromReady && toReady) {
            result.push(edge)
            pendingEdges.splice(i, 1)
          } else {
            i++
          }
        }
      } else if ('f' in op || 't' in op) {
        // Edge: check if both endpoints are already emitted (or are external)
        const from = String(op.f || '')
        const to = String(op.t || '')
        const fromReady = emittedNodeIds.has(from) || !(operations.some(o => 'c' in o && 'i' in o && !('f' in o) && !('x' in o) && String(o.i || '') === from))
        const toReady = emittedNodeIds.has(to) || !(operations.some(o => 'c' in o && 'i' in o && !('f' in o) && !('x' in o) && String(o.i || '') === to))
        if (fromReady && toReady) {
          result.push(op)
        } else {
          pendingEdges.push(op)
        }
      } else {
        // Delete or other: pass through
        result.push(op)
      }
    }

    // Append any remaining pending edges (endpoints never appeared as nodes in this batch)
    result.push(...pendingEdges)
    return result
  }

  enqueueOperation(op: GraphOperation) {
    this._processOperation(op)
    this._startDrain()
  }

  enqueueEditOperation(op: EditOperation) {
    this._processEditOperation(op)
    this._startDrain()
  }

  _resolveEntityId(id: string): string {
    return this.ids.tmpToRealMap[id] || id
  }

  _normalizeDirectionality(value?: string): GraphDirectionality | undefined {
    if (!value) {
      return undefined
    }
    if (value === direction.DIRECTED || value === direction.UNDIRECTED || value === direction.REVERSE || value === direction.BIDIRECTIONAL) {
      return value as GraphDirectionality
    }
    return undefined
  }

  _ensureKindExists(kind?: string) {
    if (!kind) {
      return
    }
    this._refreshCaches()
    if (this.existingKinds[kind]) {
      return
    }
    let color: string
    if (this.fixedThemeColors[kind]) {
      color = this.fixedThemeColors[kind] || '#CCCCCC'
    } else {
      color = this.themeColors[this.colorIdx % this.themeColors.length] || '#CCCCCC'
      this.colorIdx++
    }
    this.store.dispatch(
      createOneKind({
        id: kind,
        label: kind,
        styles: { color },
      })
    )
  }

  _processEditOperation(op: EditOperation) {
    this.completedOperations.push(op as unknown as GraphOperation)
    if (op.op === 'create_node') {
      this._ensureKindExists(op.kind)
      const entity: GraphEntity = {
        id: op.id,
        content: op.content,
      }
      if (op.kind != null) {
        entity.kind = op.kind
      }
      this.createEntity(entity)
      return
    }

    if (op.op === 'create_edge') {
      this._ensureKindExists(op.kind)
      const normalizedDirectionality = this._normalizeDirectionality(op.directionality) || direction.UNDIRECTED
      const entity: GraphEntity = {
        id: op.id,
        from: op.from,
        to: op.to,
        content: op.content || '',
        directionality: normalizedDirectionality,
      }
      if (op.kind != null) {
        entity.kind = op.kind
      }
      this.createEntity(entity)
      return
    }

    if (op.op === 'update_node') {
      const id = this._resolveEntityId(op.id)
      const payload: { id: string; content?: string; kind?: string } = { id }
      if (op.content != null) {
        payload.content = op.content
      }
      if (op.kind != null) {
        payload.kind = op.kind
        this._ensureKindExists(op.kind)
      }
      if (payload.content == null && payload.kind == null) {
        return
      }
      this._dispatchQueue.push(() => this.store.dispatch(editOneNode(payload)))
      return
    }

    if (op.op === 'update_edge') {
      const id = this._resolveEntityId(op.id)
      const payload: { id: string; content?: string; kind?: string; directionality?: string } = { id }
      if (op.content != null) {
        payload.content = op.content
      }
      if (op.kind != null) {
        payload.kind = op.kind
        this._ensureKindExists(op.kind)
      }
      const normalizedDirectionality = this._normalizeDirectionality(op.directionality)
      if (normalizedDirectionality) {
        payload.directionality = normalizedDirectionality
      }
      if (payload.content == null && payload.kind == null && payload.directionality == null) {
        return
      }
      this._dispatchQueue.push(() => this.store.dispatch(editOneEdge(payload)))
      return
    }

    if (op.op === 'delete_node') {
      this.deleteEntity('node', op.id)
      return
    }

    if (op.op === 'delete_edge') {
      this.deleteEntity('edge', op.id)
      return
    }

    if (op.op === 'update_kind') {
      this._refreshCaches()
      const existingKind = this.existingKinds[op.id] as { data?: { label?: string; styles?: Record<string, unknown> } } | undefined
      const currentLabel = String(existingKind?.data?.label || op.id)
      const currentStyles = { ...(existingKind?.data?.styles || {}) }
      if (op.color != null) {
        currentStyles.color = op.color
      }
      const nextLabel = op.label != null ? op.label : currentLabel
      this._dispatchQueue.push(() => this.store.dispatch(editOneKind({
        id: op.id,
        label: nextLabel,
        styles: currentStyles,
      })))
    }
  }

  _startDrain() {
    if (this._drainTimer != null) {
      return
    }
    this._drainTimer = setInterval(() => {
      const thunk = this._dispatchQueue.shift()
      if (thunk) {
        thunk()
      }
      if (this._dispatchQueue.length === 0) {
        clearInterval(this._drainTimer!)
        this._drainTimer = null
        const resolvers = this._drainResolvers.splice(0)
        for (const resolve of resolvers) {
          resolve()
        }
      }
    }, 250)
  }

  _drainQueue(): Promise<void> {
    if (this._dispatchQueue.length === 0 && this._drainTimer == null) {
      return Promise.resolve()
    }
    return new Promise((resolve) => {
      this._drainResolvers.push(resolve)
    })
  }

  _accumulateUsage(usage: Record<string, number>) {
    this._usage.input_tokens += usage.input_tokens || 0
    this._usage.output_tokens += usage.output_tokens || 0
    this._usage.cache_creation_input_tokens += usage.cache_creation_input_tokens || 0
    this._usage.cache_read_input_tokens += usage.cache_read_input_tokens || 0
    console.log('---USAGE---', usage)
  }

  _processOperation(op: GraphOperation) {
    // Compact format detection by key presence:
    // {"issue":"...","fix":"..."} → issue report
    // {"x":"id"} → delete
    // {"f":...,"t":...} → create edge
    // {"c":...,"k":...} → create node
    this.completedOperations.push(op)
    if ('x' in op) {
      const id = String(op.x || '')
      // Auto-detect entity type: check edges first (both existing and new), fall back to node
      this._refreshCaches()
      const realId = this.ids.tmpToRealMap[id] || id
      const isEdge = this.existingEdges[realId] != null || (this.newEntities[realId]?.from != null)
      const entityType = isEdge ? 'edge' : 'node'
      if (logging.operations) {
        console.log(`🗑️ ${entityType === 'node' ? '(' : '--['}${id}${entityType === 'node' ? ')' : ']--'}`)
      }
      this.deleteEntity(entityType, id)
    } else if ('f' in op || 't' in op) {
      const entity: GraphEntity = {
        content: String(op.c ?? ''),
        id: String(op.i ?? ''),
        from: String(op.f ?? ''),
        to: String(op.t ?? ''),
        directionality: op.d === 'D' ? 'DIRECTED' : 'UNDIRECTED',
      }
      if (op.k != null) {
        entity.kind = String(op.k)
      }
      this.createEntity(entity)
    } else if ('c' in op) {
      const entity: GraphEntity = {
        content: String(op.c ?? ''),
        id: String(op.i ?? ''),
      }
      if (op.k != null) {
        entity.kind = String(op.k)
      }
      this.createEntity(entity)
    }
  }
}

export default (store: MiddlewareStore) => (next: NextFn) => (action: ContentGenerationAction) => {
  if (isContentGenerationRequestAction(action)) {
    const generator = new ContentGenerator(store, action.mode)
    currentGenerator = generator
    generator.generateContent().then(() => {
      console.log('-------FINISHED CONTENT GENERATION--------')
      currentGenerator = null
    })
  }
  if (action.type === String(actionTypes.CONTENT_GENERATION.CANCEL)) {
    if (currentGenerator) {
      currentGenerator.cancel()
    }
  }
  return next(action)
}
